

// components/AuditHistoryDialog.js
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import TaskService from './TaskService';
import axiosInstance from '../utils/axiosInstance.js';
import './AuditHistoryDialog.css';
import API_BASE_URL from './common/common.js';

const AuditHistoryDialog = ({ taskId, onClose }) => {
  const [auditHistory, setAuditHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [allAttachments, setAllAttachments] = useState([]);
  const [imageViewer, setImageViewer] = useState({ isOpen: false, imageUrl: null, fileName: null });
  const [completionTypeMap, setCompletionTypeMap] = useState({});
  const [releaseTypeMap, setReleaseTypeMap] = useState({});
  /** Live task row from getTaskById — used to fill missing completion-file fields on the newest audit snapshot (avoids false "deleted" diffs). */
  const [liveTaskCompletionSnapshot, setLiveTaskCompletionSnapshot] = useState(null);
  const abortControllerRef = useRef(null);
  const currentTaskIdRef = useRef(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    // Prevent double execution - use synchronous flag to prevent race conditions
    if (isFetchingRef.current) {
      console.log('[AUDIT] Already fetching - skipping duplicate call for taskId:', taskId);
      return;
    }

    // Set flag immediately (synchronously) to prevent any concurrent calls
    isFetchingRef.current = true;

    // Cancel any previous request
    if (abortControllerRef.current) {
      console.log('[AUDIT] Aborting previous request');
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    currentTaskIdRef.current = taskId;
    
    console.log('✅ [AUDIT] Starting fetch for taskId:', taskId);

    const fetchAuditHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        setLiveTaskCompletionSnapshot(null);
        
        // Fetch audit history and lookup details in parallel
        const [data, lookupData] = await Promise.all([
          TaskService.getTaskAuditHistory(taskId, true),
          TaskService.fetchAllLookupDetails(true)
        ]);

        // Build completion type map from backend lookup data (id -> name)
        if (lookupData && lookupData.completion_type_details) {
          const ctMap = {};
          lookupData.completion_type_details.forEach(ct => {
            if (ct.aa_app_rl_comp_type_id) {
              ctMap[ct.aa_app_rl_comp_type_id] = ct.aa_app_rl_comp_type_name;
            }
          });
          console.log("✅ Completion type map built from backend:", ctMap);
          setCompletionTypeMap(ctMap);
        }

        if (lookupData && lookupData.release_type_details) {
          const rtMap = {};
          lookupData.release_type_details.forEach((rt) => {
            if (rt.aa_app_rl_release_type_id != null) {
              rtMap[rt.aa_app_rl_release_type_id] = rt.aa_app_rl_release_type_name;
            }
          });
          setReleaseTypeMap(rtMap);
        }
        
        // Debug: Log completion file fields in audit history
        if (data && data.length > 0) {
          console.log('📋 [DEBUG] Audit history records:', data.length);
          data.forEach((record, idx) => {
            console.log(`📋 [DEBUG] Record ${idx + 1} completion file fields:`, {
              aa_app_rl_comp_status_attach_file_name: record.aa_app_rl_comp_status_attach_file_name,
              aa_app_rl_comp_status_attach_file_type: record.aa_app_rl_comp_status_attach_file_type,
              aa_app_task_completion_file_download_url: record.aa_app_task_completion_file_download_url,
              aa_app_task_completion_file_view_url: record.aa_app_task_completion_file_view_url,
              aa_app_task_completion_file_object_key: record.aa_app_task_completion_file_object_key,
              allCompletionFields: Object.keys(record).filter(key => 
                key.toLowerCase().includes('comp') || 
                key.toLowerCase().includes('completion') ||
                key.toLowerCase().includes('file')
              )
            });
            // Log all keys to see what's available
            if (idx === 0) {
              console.log('📋 [DEBUG] All fields in first audit record:', Object.keys(record));
            }
          });
        }
        
        // Check if request was aborted before fetching task details
        if (abortController.signal.aborted) {
          console.log('🚫 [AUDIT] Request aborted before fetching task details');
          return;
        }
        
        // Fetch task details (which includes attachments) - this avoids duplicate attachment calls
        // getTaskById internally calls getTaskAttachments, so we use its attachments instead
        let attachments = [];
        let previousAttachmentsState = [];
        let taskDetails = null;
        try {
          console.log('📎 [AUDIT] Fetching task details (includes attachments) for taskId:', taskId);
          taskDetails = await TaskService.getTaskById(taskId, true);
          setLiveTaskCompletionSnapshot(taskDetails || null);
          
          // Check if request was aborted after fetching task details
          if (abortController.signal.aborted) {
            console.log('🚫 [AUDIT] Request aborted after fetching task details');
            return;
          }
          
          // Use attachments from getTaskById to avoid duplicate API call
          if (taskDetails && taskDetails.attachments) {
            attachments = taskDetails.attachments;
            previousAttachmentsState = taskDetails.attachments;
            console.log('📎 [DEBUG] Fetched attachments from getTaskById:', attachments.length);
          console.log('📎 [DEBUG] Total attachments count:', attachments.length);
          attachments.forEach((att, idx) => {
            console.log(`📎 [DEBUG] Attachment ${idx + 1}:`, {
              id: att.aa_app_task_file_attachment_id || att.id,
              name: att.aa_app_task_file_original_name || att.name,
              savedName: att.aa_app_task_file_saved_name || att.saved_name,
              uploadDate: att.aa_app_task_file_upload_date || att.upload_date || att.created_at,
              allKeys: Object.keys(att)
            });
          });
          } else {
            console.log('📎 [DEBUG] No attachments found in task details');
            }
          } catch (taskErr) {
          if (abortController.signal.aborted) {
            return;
          }
          setLiveTaskCompletionSnapshot(null);
          console.warn('📎 Could not fetch task details for history:', taskErr);
        }
        
        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        setAllAttachments(attachments);
        
        // Build attachment state for each audit record
        // IMPORTANT: We need to track ALL files that existed at each point, including deleted ones
        // Since deleted files aren't in allAttachments, we build state chronologically
        // and track what should have existed based on upload dates
        
        // Process records from oldest to newest to build cumulative state
        let allFilesEverExisted = new Map(); // Track all files by key to preserve deleted ones
        
        // First pass: collect all files that ever existed (including deleted ones)
        // We'll use upload dates to determine when files existed
        console.log('📎 [DEBUG] Building allFilesEverExisted map from', attachments.length, 'attachments');
        let skippedCount = 0;
        attachments.forEach((att, idx) => {
          const uploadDateStr = att.aa_app_task_file_upload_date || att.upload_date || att.created_at;
          if (!uploadDateStr) {
            console.log(`📎 [DEBUG] Attachment ${idx + 1} skipped - no upload date:`, {
              name: att.aa_app_task_file_original_name || att.name,
              availableFields: Object.keys(att).filter(k => k.toLowerCase().includes('date') || k.toLowerCase().includes('upload') || k.toLowerCase().includes('create'))
            });
            skippedCount++;
            return;
          }
          
          const uploadDate = new Date(uploadDateStr);
          if (!uploadDate || isNaN(uploadDate.getTime())) {
            console.log(`📎 [DEBUG] Attachment ${idx + 1} skipped - invalid date:`, {
              name: att.aa_app_task_file_original_name || att.name,
              uploadDateStr: uploadDateStr,
              parsedDate: uploadDate
            });
            skippedCount++;
            return;
          }
          
          const key = att.aa_app_task_file_attachment_id || 
                     att.id || 
                     att.aa_app_task_file_saved_name || 
                     att.saved_name ||
                     (att.aa_app_task_file_original_name ? `name_${att.aa_app_task_file_original_name}` : null);
          
          if (key && !allFilesEverExisted.has(key)) {
            allFilesEverExisted.set(key, att);
            console.log(`📎 [DEBUG] Added attachment to map:`, {
              key: key,
              name: att.aa_app_task_file_original_name || att.name,
              uploadDate: uploadDateStr,
              parsedDate: uploadDate.toISOString()
            });
          } else if (!key) {
            console.log(`📎 [DEBUG] Attachment ${idx + 1} skipped - no valid key:`, att);
            skippedCount++;
          }
        });
        console.log('📎 [DEBUG] allFilesEverExisted map size:', allFilesEverExisted.size, 'skipped:', skippedCount);
        
        // Build state for each record
        console.log('📎 [DEBUG] Processing', data.length, 'audit records');
        const enrichedData = data.map((record, index) => {
          // Get the record date
          const recordDate = new Date(record.aa_app_task_updation_date || record.aa_app_task_creation_date);
          const isCreationRecord = index === data.length - 1;
          const isFirstUpdate = index === data.length - 2 && data.length > 1;
          
          console.log(`📎 [DEBUG] Processing record ${index + 1}/${data.length}:`, {
            isCreationRecord: isCreationRecord,
            isFirstUpdate: isFirstUpdate,
            recordDate: record.aa_app_task_updation_date || record.aa_app_task_creation_date,
            parsedRecordDate: recordDate.toISOString(),
            recordType: isCreationRecord ? 'CREATION' : (isFirstUpdate ? 'FIRST_UPDATE' : 'UPDATE')
          });
          
          // Find all attachments that existed at this point in time
          // Include files that were uploaded before or at this record's date
          const allFilesArray = Array.from(allFilesEverExisted.values());
          console.log(`📎 [DEBUG] Filtering ${allFilesArray.length} attachments for record ${index + 1}`);
          
          // Get creation record date for comparison (if this is an update record)
          let creationDate = null;
          if (!isCreationRecord && data.length > 1) {
            const creationRecord = data[data.length - 1];
            creationDate = new Date(creationRecord.aa_app_task_creation_date);
          }
          
          const attachmentsAtThisTime = allFilesArray.filter(att => {
            const uploadDateStr = att.aa_app_task_file_upload_date || att.upload_date || att.created_at;
            
            // For update records (not creation), include attachments without dates if they exist
            // They might have been uploaded during creation but don't have proper timestamps
            if (!uploadDateStr) {
              if (isCreationRecord) {
                console.log(`📎 [DEBUG] Attachment filtered out (no upload date) for CREATION:`, {
                  name: att.aa_app_task_file_original_name || att.name,
                  recordIndex: index
                });
                return false;
              } else {
                // For update records, include attachments without dates (they likely existed at creation)
                console.log(`📎 [DEBUG] Including attachment without date in UPDATE record:`, {
                  name: att.aa_app_task_file_original_name || att.name,
                  recordIndex: index,
                  isFirstUpdate: isFirstUpdate
                });
                return true;
              }
            }
            
            const uploadDate = new Date(uploadDateStr);
            if (!uploadDate || isNaN(uploadDate.getTime())) {
              // Invalid date - for update records, include them (might be from creation)
              if (isCreationRecord) {
                console.log(`📎 [DEBUG] Attachment filtered out (invalid date) for CREATION:`, {
                  name: att.aa_app_task_file_original_name || att.name,
                  uploadDateStr: uploadDateStr,
                  recordIndex: index
                });
                return false;
              } else {
                console.log(`📎 [DEBUG] Including attachment with invalid date in UPDATE record:`, {
                  name: att.aa_app_task_file_original_name || att.name,
                  uploadDateStr: uploadDateStr,
                  recordIndex: index
                });
                return true;
              }
            }
            
            // Attachment existed if uploaded before or at this record's time
            const uploadTime = uploadDate.getTime();
            const recordTime = recordDate.getTime();
            
            // Use larger buffer for better tolerance (5 minutes = 300000ms)
            // This handles cases where attachments uploaded during creation have timestamps
            // very close to the creation/update timestamps
            const buffer = 300000; // 5 minute buffer
            const timeDiff = recordTime - uploadTime;
            const isIncluded = uploadTime <= (recordTime + buffer);
            
            // For update records, also check if attachment was uploaded at or before creation
            // This ensures creation attachments are always included in updates
            let isCreationAttachment = false;
            if (!isCreationRecord && creationDate) {
              const creationTime = creationDate.getTime();
              // If attachment was uploaded at or before creation (with buffer), it's a creation attachment
              isCreationAttachment = uploadTime <= (creationTime + buffer);
            }
            
            const finalIncluded = isIncluded || isCreationAttachment;
            
            if (isFirstUpdate) {
              console.log(`📎 [DEBUG] [FIRST_UPDATE] Checking attachment:`, {
                name: att.aa_app_task_file_original_name || att.name,
                uploadDate: uploadDate.toISOString(),
                recordDate: recordDate.toISOString(),
                creationDate: creationDate ? creationDate.toISOString() : 'N/A',
                uploadTime: uploadTime,
                recordTime: recordTime,
                creationTime: creationDate ? creationDate.getTime() : 'N/A',
                timeDiff: timeDiff,
                timeDiffSeconds: Math.round(timeDiff / 1000),
                buffer: buffer,
                isIncluded: isIncluded,
                isCreationAttachment: isCreationAttachment,
                finalIncluded: finalIncluded
              });
            }
            
            return finalIncluded;
          });
          
          console.log(`📎 [DEBUG] Record ${index + 1} (${isCreationRecord ? 'CREATION' : (isFirstUpdate ? 'FIRST_UPDATE' : 'UPDATE')}) has ${attachmentsAtThisTime.length} attachments:`, 
            attachmentsAtThisTime.map(a => ({
              name: a.aa_app_task_file_original_name || a.name,
              uploadDate: a.aa_app_task_file_upload_date || a.upload_date || a.created_at
            }))
          );
          
          return {
            ...record,
            attachmentsAtTime: attachmentsAtThisTime,
            recordDate: recordDate // Store for comparison
          };
        });
        
        console.log('📎 [DEBUG] Final enriched audit history summary:');
        enrichedData.forEach((r, idx) => {
          const isCreation = idx === enrichedData.length - 1;
          const isFirstUpdate = idx === enrichedData.length - 2 && enrichedData.length > 1;
          console.log(`📎 [DEBUG] Record ${idx + 1}:`, {
            type: isCreation ? 'CREATION' : (isFirstUpdate ? 'FIRST_UPDATE' : 'UPDATE'),
            date: r.aa_app_task_updation_date || r.aa_app_task_creation_date,
            attachmentCount: r.attachmentsAtTime?.length || 0,
            attachmentNames: r.attachmentsAtTime?.map(a => a.aa_app_task_file_original_name || a.name || 'Unknown') || []
          });
        });
        console.log('📎 Enriched audit history with attachment states:', enrichedData.map(r => ({
          date: r.aa_app_task_updation_date || r.aa_app_task_creation_date,
          attachmentCount: r.attachmentsAtTime?.length || 0
        })));
        
        // Check if request was aborted before setting state
        if (abortController.signal.aborted) {
          return;
        }
        
        setAuditHistory(enrichedData);
        
        // Set the most recent version as current task
        if (enrichedData && enrichedData.length > 0) {
          setCurrentTask(enrichedData[0]);
        }
        
        setLoading(false);
        isFetchingRef.current = false;
      } catch (err) {
        // Don't set error if request was aborted
        if (abortController.signal.aborted) {
          isFetchingRef.current = false;
          return;
        }
        console.error('Error fetching audit history:', err);
        setError('Failed to load audit history. Please try again later.');
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    if (taskId) {
      fetchAuditHistory();
    }

    // Cleanup function to abort request when component unmounts or taskId changes
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      currentTaskIdRef.current = null;
      isFetchingRef.current = false;
    };
  }, [taskId]);
  
  // Helper function to format date string
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'NULL' || dateStr === 'null') return 'None';
    
    try {
      // Check if date is already formatted
      if (typeof dateStr === 'string' && dateStr.includes(',')) {
        return dateStr;
      }
      
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch (e) {
      return normalizeNullValue(dateStr);
    }
  };
  
  // Helper function to convert base64 to blob (same as UpdateTaskScreen)
  const base64ToBlob = (base64String, mimeType = 'application/octet-stream') => {
    try {
      if (!base64String || base64String.trim() === '') {
        console.error('Base64 string is empty');
        return null;
      }
      
      // Remove any whitespace
      const cleanBase64 = base64String.trim();
      
      // Handle data URL format (data:mime/type;base64,actualBase64String)
      let actualBase64 = cleanBase64;
      if (cleanBase64.includes(',')) {
        const parts = cleanBase64.split(',');
        actualBase64 = parts[1];
        // Extract MIME type from data URL if available
        const mimeMatch = parts[0].match(/data:([^;]+)/);
        if (mimeMatch && mimeMatch[1]) {
          mimeType = mimeMatch[1];
        }
      }
      
      console.log('📎 Converting base64 to blob, length:', actualBase64.length, 'MIME:', mimeType);
      
      const byteCharacters = atob(actualBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      console.log('📎 Blob created successfully, size:', blob.size, 'bytes');
      return blob;
    } catch (error) {
      console.error('📎 Error converting base64 to blob:', error);
      return null;
    }
  };
  
  // Helper function to check if a value is null, undefined, empty, or "NULL"
  const hasValue = (value) => {
    return value !== null && 
           value !== undefined && 
           value !== '' && 
           value !== 'NULL' && 
           value !== 'null' &&
           String(value).trim() !== '';
  };

  // Helper function to get the correct completion type name from backend lookup data
  const getCompletionTypeName = (record) => {
    const typeId = record.aa_app_rl_comp_type_id;
    if (!typeId || typeId === 0) return record.aa_app_rl_comp_type_name || null;
    return completionTypeMap[typeId] || record.aa_app_rl_comp_type_name || null;
  };

  const normalizeReleaseTypeId = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const s = String(v).trim();
    if (s === '' || s.toUpperCase() === 'NULL') return null;
    const n = parseInt(s, 10);
    return Number.isNaN(n) || n === 0 ? null : n;
  };

  const normalizeReleaseDateKey = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const s = String(v).trim();
    if (s === '' || s.toUpperCase() === 'NULL') return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toISOString().split('T')[0];
    }
    return '';
  };

  const getReleaseTypeDisplay = (record) => {
    if (!record) return null;
    if (hasValue(record.aa_app_rl_release_type_name)) {
      return record.aa_app_rl_release_type_name;
    }
    const id = normalizeReleaseTypeId(record.aa_app_rl_release_type_id);
    if (id === null) return null;
    return releaseTypeMap[id] || `Type #${id}`;
  };

  const auditHasReleaseType = (r) => normalizeReleaseTypeId(r?.aa_app_rl_release_type_id) != null;
  const auditHasReleaseDate = (r) => normalizeReleaseDateKey(r?.aa_app_rl_release_date) !== '';

  const formatReleaseDateForChange = (v) => {
    const key = normalizeReleaseDateKey(v);
    if (!key) return 'None';
    return formatDate(key);
  };

  // Helper function to replace NULL/null with "None"
  const normalizeNullValue = (value) => {
    if (value === null || value === undefined || value === 'NULL' || value === 'null') {
      return 'None';
    }
    if (typeof value === 'string' && value.trim() === '') {
      return 'None';
    }
    return value;
  };

  // Helper function to check if file is an image
  const isImageFile = (type, fileName) => {
    if (type && type.startsWith('image/')) {
      return true;
    }
    if (fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tiff', 'tif', 'heic', 'heif'];
      return imageExtensions.includes(extension);
    }
    return false;
  };

  // Get file icon based on type or file extension
  const getFileIcon = (type, fileName) => {
    if (type) {
      if (type.includes('image')) return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
      if (type.includes('pdf')) return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
      if (type.includes('excel') || type.includes('spreadsheet')) return <i className="fas fa-file-excel" style={{ fontSize: '20px', color: '#217346' }}></i>;
      if (type.includes('word') || type.includes('msword')) return <i className="fas fa-file-word" style={{ fontSize: '20px', color: '#2196F3' }}></i>;
      if (type.includes('text')) return <i className="fas fa-file-alt" style={{ fontSize: '20px', color: '#607D8B' }}></i>;
    }
    if (fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
        return <i className="fas fa-image" style={{ fontSize: '20px', color: '#4CAF50' }}></i>;
      }
      if (extension === 'pdf') {
        return <i className="fas fa-file-pdf" style={{ fontSize: '20px', color: '#F44336' }}></i>;
      }
    }
    return <i className="fas fa-file" style={{ fontSize: '20px', color: '#757575' }}></i>;
  };
  
  // Helper function to get attachments that existed at a specific point in time
  // IMPORTANT: This only returns files that still exist in allAttachments
  // Deleted files won't be included since they're removed from allAttachments
  const getAttachmentsForRecord = (record, recordIndex, allRecords) => {
    const recordDate = new Date(record.aa_app_task_updation_date || record.aa_app_task_creation_date);
    const previousRecord = recordIndex < allRecords.length - 1 ? allRecords[recordIndex + 1] : null;
    const previousRecordDate = previousRecord 
      ? new Date(previousRecord.aa_app_task_updation_date || previousRecord.aa_app_task_creation_date)
      : null;
    
    // Check if audit record has attachments directly
    const directAttachments = record.attachments || record.file_attachments || [];
    
    // Find attachments that were uploaded before or at this record's time
    // Note: This only includes files that still exist (are in allAttachments)
    // Deleted files won't be found here
    let matchingAttachments = [];
    
    if (allAttachments.length > 0) {
      matchingAttachments = allAttachments.filter(att => {
        const uploadDateStr = att.aa_app_task_file_upload_date || att.upload_date || att.created_at;
        if (!uploadDateStr) {
          // If no upload date, include it (might be a file that was added without proper date)
          return true;
        }
        
        const uploadDate = new Date(uploadDateStr);
        if (!uploadDate || isNaN(uploadDate.getTime())) {
          console.log('📎 Invalid upload date for attachment:', att);
          // Include files without valid dates - they might have existed
          return true;
        }
        
        // Include files uploaded before or at this record's time
        // This captures all files that existed at this point AND still exist
        return uploadDate.getTime() <= (recordDate.getTime() + 60000); // 60 second buffer
      });
      
      console.log(`📎 Record ${recordIndex} (${record.aa_app_task_updation_date || record.aa_app_task_creation_date}): Found ${matchingAttachments.length} attachments that existed at this time (and still exist)`, matchingAttachments.map(a => a.aa_app_task_file_original_name || a.name || a.aa_app_task_file_saved_name));
    }
    
    // Combine both sources and remove duplicates
    const combined = [...directAttachments, ...matchingAttachments];
    const unique = [];
    const seen = new Set();
    
    combined.forEach(att => {
      const id = att.aa_app_task_file_attachment_id || att.id || att.attachment_id;
      const name = att.aa_app_task_file_saved_name || att.saved_name || att.name;
      const key = id ? `id_${id}` : (name ? `name_${name}` : null);
      
      if (key && !seen.has(key)) {
        seen.add(key);
        unique.push(att);
      }
    });
    
    return unique;
  };

  /**
   * Audit API often returns the literal string "NULL"; task details use "" or null.
   * Normalize so we never treat those as real filenames.
   */
  const parseCompletionFileName = (raw) => {
    if (raw === null || raw === undefined) return null;
    const s = String(raw).trim();
    if (s === '') return null;
    const u = s.toUpperCase();
    if (u === 'NULL' || u === 'UNDEFINED' || u === 'NONE') return null;
    return s;
  };

  const parseCompletionFileType = (raw) => {
    if (raw === null || raw === undefined) return '';
    const s = String(raw).trim();
    if (s === '') return '';
    const u = s.toUpperCase();
    if (u === 'NULL' || u === 'UNDEFINED') return '';
    return s;
  };

  const completionFileNameFromObjectKey = (record) => {
    const key = record?.aa_app_task_completion_file_object_key;
    if (!key || typeof key !== 'string') return null;
    const segment = key.split('/').pop() || key.split('\\').pop();
    return parseCompletionFileName(segment);
  };

  const getCompletionFileNameFromRecord = (record) => {
    if (!record) return null;
    const fields = [
      record.aa_app_rl_comp_status_attach_file_name,
      record.comp_status_attach_file_name,
      record.completion_file_name,
      record.aa_app_task_completion_file_name
    ];
    for (let i = 0; i < fields.length; i++) {
      const p = parseCompletionFileName(fields[i]);
      if (p) return p;
    }
    return completionFileNameFromObjectKey(record);
  };

  const normalizeCompletionFileBaseName = (name) => {
    if (!hasValue(name)) return '';
    const base = String(name).trim().toLowerCase();
    const parts = base.split(/[/\\]/);
    return parts[parts.length - 1] || base;
  };

  /**
   * Newest audit row often omits completion-file columns while the live task still has them.
   * Merge from fetchTaskDetailsById so we don't show a false "file deleted" vs the older audit row.
   */
  const mergeNewestAuditWithLiveCompletion = (auditRecord, liveTask, recordIndex) => {
    if (recordIndex !== 0 || !liveTask) return auditRecord;
    let merged = { ...auditRecord };

    if (!getCompletionFileNameFromRecord(merged)) {
      const liveName = getCompletionFileNameFromRecord(liveTask);
      if (liveName) {
        merged = {
          ...merged,
          aa_app_rl_comp_status_attach_file_name: liveName,
          aa_app_rl_comp_status_attach_file_type:
            parseCompletionFileType(liveTask.aa_app_rl_comp_status_attach_file_type) ||
            parseCompletionFileType(merged.aa_app_rl_comp_status_attach_file_type) ||
            '',
          aa_app_task_completion_file_download_url:
            liveTask.aa_app_task_completion_file_download_url ?? merged.aa_app_task_completion_file_download_url,
          aa_app_task_completion_file_view_url:
            liveTask.aa_app_task_completion_file_view_url ?? merged.aa_app_task_completion_file_view_url,
          aa_app_task_completion_file_object_key:
            liveTask.aa_app_task_completion_file_object_key ?? merged.aa_app_task_completion_file_object_key
        };
      }
    }

    if (!auditHasReleaseType(merged) && auditHasReleaseType(liveTask)) {
      merged = {
        ...merged,
        aa_app_rl_release_type_id: liveTask.aa_app_rl_release_type_id,
        aa_app_rl_release_type_name: liveTask.aa_app_rl_release_type_name ?? merged.aa_app_rl_release_type_name
      };
    }
    if (!auditHasReleaseDate(merged) && auditHasReleaseDate(liveTask)) {
      merged = {
        ...merged,
        aa_app_rl_release_date: liveTask.aa_app_rl_release_date
      };
    }

    return merged;
  };
  
  // Helper function to identify changes between audit records
  const getChanges = (currentRecord, previousRecord, currentIndex, allRecords, allAttachments = []) => {
    if (!previousRecord) return [];
    
    const changes = [];
    
    // Check for status changes
    if (currentRecord.aa_app_rl_task_status_id !== previousRecord.aa_app_rl_task_status_id) {
      changes.push({
        field: 'Status',
        from: normalizeNullValue(previousRecord.aa_app_rl_task_status_name),
        to: normalizeNullValue(currentRecord.aa_app_rl_task_status_name)
      });
    }
    
    // Check for completion percentage changes
    if (currentRecord.aa_app_rl_task_completion_percentage !== previousRecord.aa_app_rl_task_completion_percentage) {
      changes.push({
        field: 'Completion',
        from: `${previousRecord.aa_app_rl_task_completion_percentage}%`,
        to: `${currentRecord.aa_app_rl_task_completion_percentage}%`
      });
    }
    
    // Check for completion type changes
    if (currentRecord.aa_app_rl_comp_type_id !== previousRecord.aa_app_rl_comp_type_id) {
      changes.push({
        field: 'Completion Type',
        from: normalizeNullValue(getCompletionTypeName(previousRecord)),
        to: normalizeNullValue(getCompletionTypeName(currentRecord))
      });
    }
    
    // Check for completion status text description changes
    if (hasValue(currentRecord.aa_app_rl_comp_status_text_desc) || hasValue(previousRecord.aa_app_rl_comp_status_text_desc)) {
      if (currentRecord.aa_app_rl_comp_status_text_desc !== previousRecord.aa_app_rl_comp_status_text_desc) {
        changes.push({
          field: 'Completion Status Description',
          from: normalizeNullValue(previousRecord.aa_app_rl_comp_status_text_desc),
          to: normalizeNullValue(currentRecord.aa_app_rl_comp_status_text_desc)
        });
      }
    }
    
    // Check for completion file changes
    // Try multiple possible field names for completion file
    const getCompletionFileName = (record) => getCompletionFileNameFromRecord(record);
    
    const getCompletionFileType = (record) => {
      const raw =
        record.aa_app_rl_comp_status_attach_file_type ||
        record.comp_status_attach_file_type ||
        record.completion_file_type ||
        record.aa_app_task_completion_file_type ||
        '';
      return parseCompletionFileType(raw);
    };
    
    const getCompletionFileDownloadUrl = (record) => {
      return record.aa_app_task_completion_file_download_url ||
             record.completion_file_download_url ||
             record.aa_app_task_completion_file_download_url ||
             '';
    };
    
    const getCompletionFileViewUrl = (record) => {
      return record.aa_app_task_completion_file_view_url ||
             record.completion_file_view_url ||
             record.aa_app_task_completion_file_view_url ||
             '';
    };
    
    const currentCompletionFileName = getCompletionFileName(currentRecord);
    const previousCompletionFileName = getCompletionFileName(previousRecord);
    const nCurr = normalizeCompletionFileBaseName(currentCompletionFileName);
    const nPrev = normalizeCompletionFileBaseName(previousCompletionFileName);

    if (nCurr !== nPrev) {
      const hasCurrentCompletionFile = nCurr !== '';
      const hasPreviousCompletionFile = nPrev !== '';
      if (!hasPreviousCompletionFile && hasCurrentCompletionFile) {
        changes.push({
          field: 'Completion File',
          from: 'None',
          to: `${currentCompletionFileName} added`,
          isCompletionFile: true,
          completionFile: {
            name: currentCompletionFileName,
            type: getCompletionFileType(currentRecord),
            downloadUrl: getCompletionFileDownloadUrl(currentRecord),
            viewUrl: getCompletionFileViewUrl(currentRecord),
            objectKey: currentRecord.aa_app_task_completion_file_object_key || ''
          }
        });
      } else if (hasPreviousCompletionFile && !hasCurrentCompletionFile) {
        // Do not list completion file as "deleted" in Updated Fields.
      } else if (hasPreviousCompletionFile && hasCurrentCompletionFile) {
        changes.push({
          field: 'Completion File',
          from: `${previousCompletionFileName} removed`,
          to: `${currentCompletionFileName} added`,
          isCompletionFile: true,
          completionFile: {
            name: currentCompletionFileName,
            type: getCompletionFileType(currentRecord),
            downloadUrl: getCompletionFileDownloadUrl(currentRecord),
            viewUrl: getCompletionFileViewUrl(currentRecord),
            objectKey: currentRecord.aa_app_task_completion_file_object_key || ''
          }
        });
      }
    }
    
    // Check for task owner changes
    if (currentRecord.aa_app_task_owner_id !== previousRecord.aa_app_task_owner_id) {
      changes.push({
        field: 'Task Owner',
        from: normalizeNullValue(previousRecord.aa_app_task_owner_name),
        to: normalizeNullValue(currentRecord.aa_app_task_owner_name)
      });
    }
    
    // Check for assignee changes
    if (currentRecord.aa_app_task_assigned_to !== previousRecord.aa_app_task_assigned_to) {
      changes.push({
        field: 'Assigned To',
        from: normalizeNullValue(previousRecord.aa_app_task_assigned_to_name),
        to: normalizeNullValue(currentRecord.aa_app_task_assigned_to_name)
      });
    }
    
    // Check for priority changes
    if (currentRecord.aa_app_rl_resource_task_priority_id !== previousRecord.aa_app_rl_resource_task_priority_id) {
      changes.push({
        field: 'Priority',
        from: normalizeNullValue(previousRecord.aa_app_rl_priority_name),
        to: normalizeNullValue(currentRecord.aa_app_rl_priority_name)
      });
    }
    
    // Check for due date changes
    if (currentRecord.aa_app_task_due_date !== previousRecord.aa_app_task_due_date) {
      changes.push({
        field: 'Due Date',
        from: formatDate(previousRecord.aa_app_task_due_date),
        to: formatDate(currentRecord.aa_app_task_due_date)
      });
    }

    // Release type (lookup-backed name when API omits name on one side)
    if (normalizeReleaseTypeId(currentRecord.aa_app_rl_release_type_id) !==
        normalizeReleaseTypeId(previousRecord.aa_app_rl_release_type_id)) {
      changes.push({
        field: 'Release Type',
        from: normalizeNullValue(getReleaseTypeDisplay(previousRecord)),
        to: normalizeNullValue(getReleaseTypeDisplay(currentRecord))
      });
    }

    // Release date — compare normalized calendar day
    if (normalizeReleaseDateKey(currentRecord.aa_app_rl_release_date) !==
        normalizeReleaseDateKey(previousRecord.aa_app_rl_release_date)) {
      changes.push({
        field: 'Release Date',
        from: formatReleaseDateForChange(previousRecord.aa_app_rl_release_date),
        to: formatReleaseDateForChange(currentRecord.aa_app_rl_release_date)
      });
    }
    
    // Check for title changes
    if (currentRecord.aa_app_rl_resource_task_title !== previousRecord.aa_app_rl_resource_task_title) {
      changes.push({
        field: 'Title',
        from: normalizeNullValue(previousRecord.aa_app_rl_resource_task_title),
        to: normalizeNullValue(currentRecord.aa_app_rl_resource_task_title)
      });
    }
    
    // Check for description changes
    if (currentRecord.aa_app_rl_resource_task_desc !== previousRecord.aa_app_rl_resource_task_desc) {
      changes.push({
        field: 'Description',
        from: normalizeNullValue(previousRecord.aa_app_rl_resource_task_desc),
        to: normalizeNullValue(currentRecord.aa_app_rl_resource_task_desc)
      });
    }
    
    // Check for application changes
    if (currentRecord.aa_app_rl_application_id !== previousRecord.aa_app_rl_application_id) {
      changes.push({
        field: 'Application',
        from: normalizeNullValue(previousRecord.aa_app_rl_application_name),
        to: normalizeNullValue(currentRecord.aa_app_rl_application_name)
      });
    }
    
    // Check for task type changes
    if (currentRecord.aa_app_rl_task_type_id !== previousRecord.aa_app_rl_task_type_id) {
      changes.push({
        field: 'Task Type',
        from: normalizeNullValue(previousRecord.aa_app_rl_task_type_name) || 'Unknown',
        to: normalizeNullValue(currentRecord.aa_app_rl_task_type_name) || 'Unknown'
      });
    }
    
    // Check for updates to description - only if both values are not null/empty
    if (hasValue(currentRecord.aa_app_rl_task_status_update_desc) || hasValue(previousRecord.aa_app_rl_task_status_update_desc)) {
      if (currentRecord.aa_app_rl_task_status_update_desc !== previousRecord.aa_app_rl_task_status_update_desc) {
        changes.push({
          field: 'Update Description',
          from: normalizeNullValue(previousRecord.aa_app_rl_task_status_update_desc),
          to: normalizeNullValue(currentRecord.aa_app_rl_task_status_update_desc)
        });
      }
    }
    
    // Check for updates to issues - only if both values are not null/empty
    if (hasValue(currentRecord.aa_app_rl_task_status_issue_desc) || hasValue(previousRecord.aa_app_rl_task_status_issue_desc)) {
      if (currentRecord.aa_app_rl_task_status_issue_desc !== previousRecord.aa_app_rl_task_status_issue_desc) {
        changes.push({
          field: 'Issue Description',
          from: normalizeNullValue(previousRecord.aa_app_rl_task_status_issue_desc),
          to: normalizeNullValue(currentRecord.aa_app_rl_task_status_issue_desc)
        });
      }
    }
    
    // Check for attachment changes (files added/removed)
    // Use before/after state comparison - much more reliable than date-based matching
    if (allRecords) {
      const isFirstUpdate = currentIndex === allRecords.length - 2 && allRecords.length > 1;
      
      // Get attachment states: before (previous record) and after (current record)
      const previousAttachments = previousRecord.attachmentsAtTime || [];
      const currentAttachments = currentRecord.attachmentsAtTime || [];
      
      // Also check if audit records have attachment info directly
      const previousRecordAttachments = previousRecord.attachments || previousRecord.file_attachments || [];
      const currentRecordAttachments = currentRecord.attachments || currentRecord.file_attachments || [];
      
      // Combine both sources for more complete state
      const previousState = [...previousAttachments, ...previousRecordAttachments];
      const currentState = [...currentAttachments, ...currentRecordAttachments];
      
      console.log(`📎 [DEBUG] [getChanges] Record ${currentIndex} attachment state comparison:`, {
        isFirstUpdate: isFirstUpdate,
        previousAttachmentsFromTime: previousAttachments.length,
        currentAttachmentsFromTime: currentAttachments.length,
        previousRecordAttachments: previousRecordAttachments.length,
        currentRecordAttachments: currentRecordAttachments.length,
        previousStateTotal: previousState.length,
        currentStateTotal: currentState.length,
        previousFiles: previousState.map(a => ({
          name: a.aa_app_task_file_original_name || a.name || a.aa_app_task_file_saved_name,
          id: a.aa_app_task_file_attachment_id || a.id,
          uploadDate: a.aa_app_task_file_upload_date || a.upload_date || a.created_at
        })),
        currentFiles: currentState.map(a => ({
          name: a.aa_app_task_file_original_name || a.name || a.aa_app_task_file_saved_name,
          id: a.aa_app_task_file_attachment_id || a.id,
          uploadDate: a.aa_app_task_file_upload_date || a.upload_date || a.created_at
        })),
        previousRecordDate: previousRecord.aa_app_task_updation_date || previousRecord.aa_app_task_creation_date,
        currentRecordDate: currentRecord.aa_app_task_updation_date || currentRecord.aa_app_task_creation_date
      });
      
      if (isFirstUpdate) {
        console.log(`📎 [DEBUG] [FIRST_UPDATE] Detailed attachment analysis:`, {
          previousAttachmentsDetail: previousAttachments.map(a => ({
            name: a.aa_app_task_file_original_name || a.name,
            id: a.aa_app_task_file_attachment_id || a.id,
            uploadDate: a.aa_app_task_file_upload_date || a.upload_date || a.created_at,
            allKeys: Object.keys(a)
          })),
          currentAttachmentsDetail: currentAttachments.map(a => ({
            name: a.aa_app_task_file_original_name || a.name,
            id: a.aa_app_task_file_attachment_id || a.id,
            uploadDate: a.aa_app_task_file_upload_date || a.upload_date || a.created_at,
            allKeys: Object.keys(a)
          })),
          allAttachmentsCount: allAttachments.length,
          allAttachmentsNames: allAttachments.map(a => a.aa_app_task_file_original_name || a.name || a.aa_app_task_file_saved_name)
        });
      }
      
      // Helper function to get attachment key for comparison
      const getAttachmentKey = (a) => {
        return a.aa_app_task_file_attachment_id || 
               a.id || 
               a.attachment_id ||
               a.aa_app_task_file_saved_name || 
               a.saved_name || 
               (a.aa_app_task_file_original_name ? `name_${a.aa_app_task_file_original_name}` : null) ||
               (a.name ? `name_${a.name}` : null);
      };
      
      // Build sets of attachment keys for efficient comparison
      const previousKeys = new Set(previousState.map(att => getAttachmentKey(att)).filter(Boolean));
      const currentKeys = new Set(currentState.map(att => getAttachmentKey(att)).filter(Boolean));
      
      // Find files that were ADDED: exist in current but not in previous
      // OR were uploaded between previous record time and current record time
      const previousRecordTime = previousRecord.recordDate ? previousRecord.recordDate.getTime() : null;
      const currentRecordTime = currentRecord.recordDate ? currentRecord.recordDate.getTime() : null;
      const buffer = 300000; // 5 minute buffer for timing differences
      
      const addedFiles = currentState.filter(att => {
        const key = getAttachmentKey(att);
        if (!key) return false;
        
        // First check: file doesn't exist in previous state (simple comparison)
        // This is the primary way to detect new files
        if (!previousKeys.has(key)) {
          console.log(`📎 Detected new attachment (not in previous state):`, {
            fileName: att.aa_app_task_file_original_name || att.name,
            key
          });
          return true;
        }
        
        // Second check: file was uploaded BETWEEN previous and current record times
        // This catches cases where the same file might appear in both states but was actually uploaded during this update
        if (previousRecordTime && currentRecordTime && previousRecordTime < currentRecordTime) {
          const uploadDateStr = att.aa_app_task_file_upload_date || att.upload_date || att.created_at;
          if (uploadDateStr) {
            const uploadDate = new Date(uploadDateStr);
            if (uploadDate && !isNaN(uploadDate.getTime())) {
              const uploadTime = uploadDate.getTime();
              
              // File was uploaded after the previous record time (with buffer) and at or before current record time
              // This means it was added during this specific update
              const uploadedAfterPrevious = uploadTime > (previousRecordTime - buffer);
              const uploadedBeforeCurrent = uploadTime <= (currentRecordTime + buffer);
              
              if (uploadedAfterPrevious && uploadedBeforeCurrent) {
                // Double-check: if file exists in previous state, only count as "added" if upload time is clearly after previous
                // This prevents counting creation attachments as "added" in updates
                if (uploadTime > previousRecordTime) {
                  console.log(`📎 Detected attachment added during update (by upload date):`, {
                    fileName: att.aa_app_task_file_original_name || att.name,
                    uploadTime: new Date(uploadTime).toISOString(),
                    previousRecordTime: new Date(previousRecordTime).toISOString(),
                    currentRecordTime: new Date(currentRecordTime).toISOString(),
                    timeDiff: Math.round((uploadTime - previousRecordTime) / 1000) + ' seconds'
                  });
                  return true;
                }
              }
            }
          }
        }
        
        return false;
      });
      
      // Find files that were DELETED: exist in previous but not in current
      // CRITICAL: A file was deleted if it existed in previousState but not in currentState
      // Store deleted filenames in a variable for tracking
      const deletedFiles = previousState.filter(att => {
        const key = getAttachmentKey(att);
        if (!key) return false;
        
        // File exists in previous but not in current = deleted
        const wasDeleted = !currentKeys.has(key);
        
        if (wasDeleted) {
          const fileName = att.aa_app_task_file_original_name || att.name || att.aa_app_task_file_saved_name || 'Unknown';
          console.log(`📎 Detected deleted file: ${fileName}`, {
            key,
            previousRecordDate: previousRecord.recordDate,
            currentRecordDate: currentRecord.recordDate,
            fileInfo: att
          });
        }
        
        return wasDeleted;
      });
      
      // Store deleted filenames for this record
      const deletedFileNames = deletedFiles.map(file => {
        return file.aa_app_task_file_original_name || file.name || file.file_name || file.aa_app_task_file_saved_name || 'Unknown file';
      });
      
      console.log(`📎 Attachment changes for record ${currentIndex}:`, {
        added: addedFiles.length,
        deleted: deletedFiles.length,
        addedFiles: addedFiles.map(f => f.aa_app_task_file_original_name || f.name || f.aa_app_task_file_saved_name),
        deletedFiles: deletedFileNames
      });
      
      // Add attachment changes - ONLY added/deleted files
      if (addedFiles.length > 0) {
        addedFiles.forEach(file => {
          const fileName = file.aa_app_task_file_original_name || file.name || file.file_name || file.aa_app_task_file_saved_name || 'Unknown file';
          
          // Try to find the complete file object from allAttachments
          // This ensures we have all necessary fields (savedFileName, base64Content, etc.)
          // Note: getAttachmentKey is already defined above in this function
          const fileKey = getAttachmentKey(file);
          let completeFile = file; // Default to the file from state comparison
          
          // Try to find a more complete version in allAttachments
          if (fileKey && allAttachments && allAttachments.length > 0) {
            const foundComplete = allAttachments.find(att => {
              const attKey = getAttachmentKey(att);
              if (attKey && attKey === fileKey) return true;
              
              // Also try name matching (case-insensitive, filename only)
              const attName = att.aa_app_task_file_original_name || att.name || att.file_name;
              const fileNameOnly = fileName.split('/').pop().split('\\').pop();
              const attNameOnly = attName ? attName.split('/').pop().split('\\').pop() : '';
              if (attNameOnly && fileNameOnly && attNameOnly.toLowerCase() === fileNameOnly.toLowerCase()) return true;
              
              return false;
            });
            
            if (foundComplete) {
              completeFile = foundComplete;
              console.log('📎 Found complete file object in allAttachments:', {
                fileName,
                hasSavedName: !!foundComplete.aa_app_task_file_saved_name,
                hasBase64: !!foundComplete.aa_app_task_file_content_base64,
                fileType: foundComplete.aa_app_task_file_type || foundComplete.type
              });
            } else {
              console.log('📎 Using file from state comparison (not found in allAttachments):', {
                fileName,
                fileKey,
                allAttachmentCount: allAttachments.length,
                allAttachmentKeys: allAttachments.map(a => getAttachmentKey(a)).filter(Boolean)
              });
            }
          }
          
          changes.push({
            field: 'Attachments',
            from: '',
            to: `${fileName} added`,
            isAttachment: true,
            attachment: completeFile, // Store complete attachment object for download/view functionality
            fileName: fileName // Store filename separately for easy access
          });
        });
      }
      
      // Display deleted files as "filename deleted"
      // Store deleted filenames and display them in history
      if (deletedFiles.length > 0) {
        deletedFiles.forEach(file => {
          // Store the deleted filename in a variable for tracking
          const deletedFileName = file.aa_app_task_file_original_name || file.name || file.file_name || file.aa_app_task_file_saved_name || 'Unknown file';
          
          console.log(`📎 Adding deleted file to history: ${deletedFileName}`);
          
          changes.push({
            field: 'Attachments',
            from: '',
            to: `${deletedFileName} deleted`,
            isAttachment: true,
            attachment: file,
            isRemoved: true,
            deletedFileName: deletedFileName // Store deleted filename for reference
          });
        });
      } else {
        // Log when no deletions are detected (for debugging)
        if (previousState.length > currentState.length) {
          console.log(`📎 Potential deletion detected but not captured:`, {
            previousCount: previousState.length,
            currentCount: currentState.length,
            previousFiles: previousState.map(a => a.aa_app_task_file_original_name || a.name || a.aa_app_task_file_saved_name),
            currentFiles: currentState.map(a => a.aa_app_task_file_original_name || a.name || a.aa_app_task_file_saved_name)
          });
        }
      }
    }
    
    return changes;
  };
  
  if (loading) {
    return createPortal(
      <div
        className="audit-dialog-overlay"
        role="presentation"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="audit-dialog" onMouseDown={(e) => e.stopPropagation()}>
          <div className="audit-dialog-header">
            <h2>Task History</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="audit-dialog-content loading">
            <div className="loading-spinner"></div>
            <p>Loading history...</p>
          </div>
        </div>
      </div>,
      document.body
    );
  }
  
  if (error) {
    return createPortal(
      <div
        className="audit-dialog-overlay"
        role="presentation"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="audit-dialog" onMouseDown={(e) => e.stopPropagation()}>
          <div className="audit-dialog-header">
            <h2>Task History</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="audit-dialog-content">
            <div className="error-message">{error}</div>
          </div>
        </div>
      </div>,
      document.body
    );
  }
  
  return createPortal(
    <div
      className="audit-dialog-overlay"
      role="presentation"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="audit-dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="audit-dialog-header">
          <h2>Task History - {currentTask?.aa_app_rl_resource_task_title}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="audit-dialog-content">
          {auditHistory.length === 0 ? (
            <p className="no-history">No history records found for this task.</p>
          ) : (
            <div className="task-timeline">
              {(() => {
                const visibleAuditEntries = auditHistory
                  .map((record, index) => {
                    const nextRecord = auditHistory[index + 1];
                    const recordForCompare = mergeNewestAuditWithLiveCompletion(
                      record,
                      liveTaskCompletionSnapshot,
                      index
                    );
                    const changes = getChanges(
                      recordForCompare,
                      nextRecord,
                      index,
                      auditHistory,
                      allAttachments
                    );
                    const isCreation = index === auditHistory.length - 1;
                    const showRow = isCreation || changes.length > 0;
                    return { record, index, changes, recordForCompare, isCreation, showRow };
                  })
                  .filter((e) => e.showRow);

                return visibleAuditEntries.map((entry, displayIndex) => {
                  const { record, index, changes, isCreation } = entry;

                  const attachmentChanges = changes.filter((c) => c.isAttachment);
                  if (attachmentChanges.length > 0) {
                    console.log(
                      `📎 [RENDER] Record ${index} has ${attachmentChanges.length} attachment changes:`,
                      attachmentChanges
                    );
                  }

                  const isCreatedBadge = isCreation;
                  const isCurrentBadge = displayIndex === 0 && !isCreation;

                  return (
                  <div key={record.aa_app_task_audit_id} className="timeline-item">
                    <div className="timeline-marker">
                      <div className="timeline-icon"></div>
                      <div className="timeline-line"></div>
                    </div>
                    
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timestamp">{formatDate(record.aa_app_task_updation_date)}</span>
                        
                        {isCurrentBadge && (
                          <span className="current-tag">Current</span>
                        )}
                        
                        {isCreatedBadge && (
                          <span className="created-tag">Created</span>
                        )}
                      </div>
                      
                      <div className="timeline-body">
                        {isCreation ? (
                          // For the first record (task creation)
                          <div className="creation-info">
                            <p><strong>Task Created</strong> by {normalizeNullValue(record.aa_app_task_created_by_name)}</p>
                            <p><strong>Task Owner:</strong> {normalizeNullValue(record.aa_app_task_owner_name)}</p>
                            <p><strong>Title:</strong> {normalizeNullValue(record.aa_app_rl_resource_task_title)}</p>
                            <p><strong>Description:</strong> {normalizeNullValue(record.aa_app_rl_resource_task_desc) || 'No description'}</p>
                            <p><strong>Application:</strong> {normalizeNullValue(record.aa_app_rl_application_name)}</p>
                            <p><strong>Assigned to:</strong> {normalizeNullValue(record.aa_app_task_assigned_to_name)}</p>
                            <p><strong>Priority:</strong> {normalizeNullValue(record.aa_app_rl_priority_name)}</p>
                            <p><strong>Status:</strong> {normalizeNullValue(record.aa_app_rl_task_status_name)}</p>
                            {hasValue(record.aa_app_rl_task_completion_percentage) && (
                              <p><strong>Completion:</strong> {record.aa_app_rl_task_completion_percentage}%</p>
                            )}
                            {hasValue(getCompletionTypeName(record)) && (
                              <p><strong>Completion Type:</strong> {normalizeNullValue(getCompletionTypeName(record))}</p>
                            )}
                            {hasValue(record.aa_app_rl_comp_status_text_desc) && (
                              <p><strong>Completion Status Description:</strong> {normalizeNullValue(record.aa_app_rl_comp_status_text_desc)}</p>
                            )}
                            {(() => {
                              const completionFileName = getCompletionFileNameFromRecord(record);
                              const completionFileType = parseCompletionFileType(
                                record.aa_app_rl_comp_status_attach_file_type ||
                                  record.comp_status_attach_file_type ||
                                  record.completion_file_type ||
                                  record.aa_app_task_completion_file_type ||
                                  ''
                              );
                              const completionFileDownloadUrl = record.aa_app_task_completion_file_download_url ||
                                                               record.completion_file_download_url ||
                                                               '';
                              const completionFileViewUrl = record.aa_app_task_completion_file_view_url ||
                                                           record.completion_file_view_url ||
                                                           '';
                              
                              if (hasValue(completionFileName)) {
                                return (
                                  <p>
                                    <strong>Completion File:</strong>{' '}
                                    {completionFileDownloadUrl ? (
                                      <a
                                        href={completionFileDownloadUrl}
                                        download={completionFileName}
                                        onClick={async (e) => {
                                          e.preventDefault();
                                          try {
                                            const response = await axiosInstance.get(completionFileDownloadUrl, {
                                              responseType: 'blob',
                                              timeout: 30000
                                            });
                                            
                                            if (!response.data || response.data.size === 0) {
                                              throw new Error('Downloaded file is empty');
                                            }
                                            
                                            const blob = new Blob([response.data], { 
                                              type: completionFileType || response.headers['content-type'] || 'application/octet-stream' 
                                            });
                                            const blobUrl = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = blobUrl;
                                            link.download = completionFileName;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            
                                            // Cleanup
                                            setTimeout(() => {
                                              window.URL.revokeObjectURL(blobUrl);
                                            }, 100);
                                          } catch (error) {
                                            console.error('Error downloading completion file:', error);
                                            alert('Failed to download file. Please try again.');
                                          }
                                        }}
                                        style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                                      >
                                        {completionFileName}
                                      </a>
                                    ) : (
                                      <span>{completionFileName}</span>
                                    )}
                                    {isImageFile(completionFileType, completionFileName) && 
                                     completionFileViewUrl && (
                                      <button
                                        className="inline-view-btn"
                                        title="View file"
                                        onClick={async (e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          try {
                                            const response = await axiosInstance.get(completionFileViewUrl, {
                                              responseType: 'blob',
                                              timeout: 30000
                                            });
                                            
                                            const blob = new Blob([response.data], { 
                                              type: completionFileType || response.headers['content-type'] || 'image/png'
                                            });
                                            
                                            const blobUrl = window.URL.createObjectURL(blob);
                                            setImageViewer({
                                              isOpen: true,
                                              imageUrl: blobUrl,
                                              fileName: completionFileName
                                            });
                                          } catch (error) {
                                            console.error('Error viewing completion file:', error);
                                            alert('Failed to view file. Please try downloading it instead.');
                                          }
                                        }}
                                        style={{ marginLeft: '8px' }}
                                      >
                                        <i className="fas fa-eye"></i>
                                      </button>
                                    )}
                                  </p>
                                );
                              }
                              return null;
                            })()}
                            <p><strong>Due Date:</strong> {formatDate(record.aa_app_task_due_date)}</p>
                            {(auditHasReleaseType(record) || hasValue(record.aa_app_rl_release_type_name)) && (
                              <p><strong>Release Type:</strong> {normalizeNullValue(getReleaseTypeDisplay(record))}</p>
                            )}
                            {auditHasReleaseDate(record) && (
                              <p><strong>Release Date:</strong> {formatReleaseDateForChange(record.aa_app_rl_release_date)}</p>
                            )}
                          </div>
                        ) : (
                          // For subsequent records (task updates)
                          <div>
                            <p><strong>Updated by:</strong> {record.aa_app_task_updated_by_name}</p>
                            
                            {/* Show only fields that were updated */}
                            {changes.length > 0 && (
                              <div className="changes-list">
                                <p><strong>Updated Fields:</strong></p>
                                <ul>
                                  {changes.map((change, changeIndex) => (
                                    <li key={changeIndex}>
                                      <strong>{change.field}:</strong> {
                                        change.isCompletionFile ? (
                                          // For completion file changes, show with download link and view icon (for images only)
                                          <span className="attachment-change-item">
                                            {change.isRemoved ? (
                                              // For deleted files, just show text (file doesn't exist anymore)
                                              <span>{change.from || `${change.completionFile?.name || 'File'} deleted`}</span>
                                            ) : (
                                              // For added/changed files, show filename as download link + view icon (if image)
                                              (() => {
                                                try {
                                                  const completionFile = change.completionFile;
                                                  if (!completionFile) {
                                                    return <span>{change.to || 'Completion file'}</span>;
                                                  }
                                                  
                                                  const fileName = completionFile.name || 'Unknown file';
                                                  const fileType = completionFile.type || '';
                                                  const downloadUrl = completionFile.downloadUrl || '';
                                                  const viewUrl = completionFile.viewUrl || '';
                                                  
                                                  // Check if it's an image
                                                  const isImage = isImageFile(fileType, fileName);
                                                  
                                                  return (
                                                    <span className="attachment-with-actions">
                                                      {/* Filename as plain text (not clickable) */}
                                                      <span style={{ cursor: 'default', textDecoration: 'none' }}>
                                                          {fileName}
                                                      </span>
                                                    </span>
                                                  );
                                                } catch (error) {
                                                  console.error('Error rendering completion file:', error, change);
                                                  return <span>{change.to || 'Completion file'}</span>;
                                                }
                                              })()
                                            )}
                                          </span>
                                        ) : change.isAttachment ? (
                                          // For attachment changes, show with download link and view icon (for images only)
                                          <span className="attachment-change-item">
                                            {change.isRemoved ? (
                                              // For deleted files, just show text (file doesn't exist anymore)
                                              <span>{change.to || `${change.deletedFileName || 'File'} deleted`}</span>
                                            ) : (
                                              // For added files, show filename as download link + view icon (if image)
                                              (() => {
                                                try {
                                                const file = change.attachment;
                                                
                                                // Extract filename and file info
                                                let fileName = 'Unknown file';
                                                let fileType = '';
                                                let savedFileName = null;
                                                let base64Content = null;
                                                
                                                // Helper function to find file in an array
                                                const findFileInArray = (arr, searchFileName) => {
                                                  return arr.find(att => {
                                                    const attName = att.aa_app_task_file_original_name || att.name || att.file_name || att.aa_app_task_file_saved_name;
                                                    if (!attName) return false;
                                                    // Try exact match first
                                                    if (attName === searchFileName) return true;
                                                    // Try case-insensitive match
                                                    if (attName.toLowerCase() === searchFileName.toLowerCase()) return true;
                                                    // Try partial match (filename without path)
                                                    const attNameOnly = attName.split('/').pop().split('\\').pop();
                                                    const fileNameOnly = searchFileName.split('/').pop().split('\\').pop();
                                                    if (attNameOnly === fileNameOnly || attNameOnly.toLowerCase() === fileNameOnly.toLowerCase()) return true;
                                                    return false;
                                                  });
                                                };
                                                
                                                if (file) {
                                                  // Use attachment object if available
                                                  fileName = file.aa_app_task_file_original_name || file.name || file.file_name || file.aa_app_task_file_saved_name || 'Unknown file';
                                                  fileType = file.aa_app_task_file_type || file.type || file.mime_type || '';
                                                  savedFileName = file.aa_app_task_file_saved_name || file.saved_name;
                                                  base64Content = file.aa_app_task_file_content_base64 || file.base64_content;
                                                } else {
                                                  // Extract filename from change.to (format: "filename.ext added")
                                                  if (change.to) {
                                                    const match = change.to.match(/^(.+?)\s+added$/);
                                                    fileName = match ? match[1] : change.to.replace(' added', '');
                                                  } else if (change.fileName) {
                                                    fileName = change.fileName;
                                                  }
                                                  
                                                  // Try to find the file in multiple sources:
                                                  // 1. Current record's attachments
                                                  const recordAttachments = record.attachments || record.file_attachments || [];
                                                  let foundFile = findFileInArray(recordAttachments, fileName);
                                                  
                                                  // 2. If not found, try allAttachments
                                                  if (!foundFile) {
                                                    foundFile = findFileInArray(allAttachments, fileName);
                                                  }
                                                  
                                                  if (foundFile) {
                                                    fileType = foundFile.aa_app_task_file_type || foundFile.type || foundFile.mime_type || '';
                                                    savedFileName = foundFile.aa_app_task_file_saved_name || foundFile.saved_name;
                                                    base64Content = foundFile.aa_app_task_file_content_base64 || foundFile.base64_content;
                                                    console.log('📎 Found file:', {
                                                      fileName,
                                                      foundFileName: foundFile.aa_app_task_file_original_name || foundFile.name,
                                                      savedFileName,
                                                      hasBase64: !!base64Content,
                                                      source: foundFile === findFileInArray(recordAttachments, fileName) ? 'record' : 'allAttachments'
                                                    });
                                                  } else {
                                                    console.log('📎 File not found:', {
                                                      fileName,
                                                      recordAttachments: recordAttachments.length,
                                                      allAttachments: allAttachments.length,
                                                      recordAttachmentNames: recordAttachments.map(a => a.aa_app_task_file_original_name || a.name || a.aa_app_task_file_saved_name),
                                                      allAttachmentNames: allAttachments.map(a => a.aa_app_task_file_original_name || a.name || a.aa_app_task_file_saved_name)
                                                    });
                                                  }
                                                }
                                                
                                                // Build download URL - try multiple methods like UpdateTaskScreen
                                                let downloadUrl = null;
                                                
                                                // Helper function to build URL with optional task ID
                                                const buildDownloadUrl = (paramName, paramValue) => {
                                                  if (!paramValue) return null;
                                                  let url = `${API_BASE_URL}/downloadTaskAttachment?${paramName}=${encodeURIComponent(paramValue)}`;
                                                  if (taskId) {
                                                    url += `&aa_app_rl_resource_task_uid=${taskId}`;
                                                  }
                                                  return url;
                                                };
                                                
                                                // Get attachment ID if available
                                                const attachmentId = file?.aa_app_task_file_attachment_id || file?.id || file?.attachment_id;
                                                
                                                // First priority: Use savedFileName (most reliable for file access)
                                                if (savedFileName) {
                                                  downloadUrl = buildDownloadUrl('aa_app_task_file_saved_name', savedFileName);
                                                } 
                                                // Second priority: Use attachment ID if it's not 0, null, or undefined
                                                else if (attachmentId !== null && attachmentId !== undefined && attachmentId !== 0) {
                                                  downloadUrl = buildDownloadUrl('aa_app_task_file_attachment_id', attachmentId);
                                                }
                                                // Third priority: Try using fileName directly
                                                else if (fileName && fileName !== 'Unknown file') {
                                                  downloadUrl = buildDownloadUrl('filename', fileName);
                                                }
                                                
                                                // Try alternative field names for URL
                                                if (!downloadUrl && file) {
                                                  downloadUrl = file.url || file.file_url || file.attachment_url || file.fileUrl || file.download_url || file.downloadUrl;
                                                }
                                                
                                                // Ensure fileType is properly set for images
                                                if (!fileType && fileName) {
                                                  const ext = fileName.split('.').pop().toLowerCase();
                                                  const imageMimeTypes = {
                                                    'png': 'image/png',
                                                    'jpg': 'image/jpeg',
                                                    'jpeg': 'image/jpeg',
                                                    'gif': 'image/gif',
                                                    'webp': 'image/webp',
                                                    'bmp': 'image/bmp',
                                                    'svg': 'image/svg+xml',
                                                    'ico': 'image/x-icon',
                                                    'tiff': 'image/tiff',
                                                    'tif': 'image/tiff'
                                                  };
                                                  if (imageMimeTypes[ext]) {
                                                    fileType = imageMimeTypes[ext];
                                                  }
                                                }
                                                
                                                // Check if it's an image
                                                const isImage = isImageFile(fileType, fileName);
                                                
                                                console.log('📎 Rendering attachment in history:', {
                                                  fileName,
                                                  fileType,
                                                  savedFileName,
                                                  hasBase64: !!base64Content,
                                                  hasDownloadUrl: !!downloadUrl,
                                                  isImage,
                                                  hasAttachmentObject: !!file
                                                });
                                                
                                                return (
                                                  <span className="attachment-with-actions">
                                                    {/* Filename as plain text (not clickable for attachments) */}
                                                    <span style={{ cursor: 'default', textDecoration: 'none', color: 'inherit' }}>
                                                        {fileName}
                                                    </span>
                                                  </span>
                                                );
                                                } catch (error) {
                                                  console.error('Error rendering attachment:', error, change);
                                                  // Fallback: just show the filename from change.to or change.fileName (not clickable for attachments)
                                                  const fallbackFileName = change.fileName || (change.to ? change.to.replace(' added', '') : 'Unknown file');
                                                  return (
                                                    <span className="attachment-with-actions">
                                                      <span style={{ cursor: 'default', textDecoration: 'none', color: 'inherit' }}>
                                                        {fallbackFileName}
                                                      </span>
                                                    </span>
                                                  );
                                                }
                                              })()
                                            )}
                                          </span>
                                        ) : (
                                          // For regular field changes
                                          <span>{normalizeNullValue(change.from)} → {normalizeNullValue(change.to)}</span>
                                        )
                                      }
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {hasValue(record.aa_app_rl_task_status_update_desc) && (
                              <div className="update-note">
                                <p><strong>Status Update:</strong></p>
                                <p className="note-content">{record.aa_app_rl_task_status_update_desc}</p>
                              </div>
                            )}
                            
                            {hasValue(record.aa_app_rl_task_status_issue_desc) && (
                              <div className="issue-note">
                                <p><strong>Issue:</strong></p>
                                <p className="note-content">{record.aa_app_rl_task_status_issue_desc}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
                });
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      {imageViewer.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10060,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => {
            if (imageViewer.imageUrl) {
              window.URL.revokeObjectURL(imageViewer.imageUrl);
            }
            setImageViewer({ isOpen: false, imageUrl: null, fileName: null });
          }}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                if (imageViewer.imageUrl) {
                  window.URL.revokeObjectURL(imageViewer.imageUrl);
                }
                setImageViewer({ isOpen: false, imageUrl: null, fileName: null });
              }}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#333',
                fontWeight: 'bold',
                zIndex: 10002
              }}
              title="Close"
            >
              ×
            </button>
            {imageViewer.imageUrl && (
              <img
                src={imageViewer.imageUrl}
                alt={imageViewer.fileName || 'Image'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                }}
              />
            )}
            {imageViewer.fileName && (
              <div
                style={{
                  marginTop: '10px',
                  color: 'white',
                  fontSize: '14px',
                  textAlign: 'center',
                  maxWidth: '100%',
                  wordBreak: 'break-word'
                }}
              >
                {imageViewer.fileName}
              </div>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default AuditHistoryDialog;